import React, {Fragment, useEffect, useState} from 'react';
import {
    ListGroup,
    ListGroupItem,
    Button,
    ButtonGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {postNewVersion, deleteVersion, updateVersion} from '../../slices/movies.slice';

import VersionForm from './VersionForm';
import TierBadge from '../../components/TierBadge';
import Avatar from '../../components/Avatar';
import {imgUrl} from '../../utils/api-request';
import {formatDate, formatRegions} from '../../utils/dates';
import './VersionList.scss';
import { fetchOrganizations } from '../../slices/organizations.slice';
import {toast} from 'react-toastify';
import { errorToast } from '../../utils/errorToast';

const emptyVersionData = {
    name: '',
    uuid: '',
    tier: ['mid', 'premium'],
    countries: [],
    organizations: [],
    conditionalPrice: { mid: [], premium: [] },
    territoryPrices: [],
    contentRestrictions: null,
    releaseDate: '',
    isMovieOnRequest: false,
    assetDetails: null,
};

const VersionsList = ({ versions, movie, distributor = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const organizations = useSelector((state) => state.organizations.list);
    const token = useSelector(state => state.auth.token);

    // Parse versionId from URL path: /movies/:movieId/version/:versionId
    const getVersionIdFromPath = () => {
        const match = location.pathname.match(/\/movies\/[^/]+\/version\/(.+)/);
        return match ? match[1] : null;
    };

    const versionId = getVersionIdFromPath();
    // Derive selectedVersionId from URL
    const selectedVersionId = versionId === 'new' ? 'new-version' : versionId || null;

    const [deleteModal, setDeleteModal] = useState({ isOpen: false, version: null });
    const [organizationsModal, setOrganizationsModalOpen] = useState(false);
    const [countriesModal, setCountriesModalOpen] = useState(false);
    const [newVersionData, setNewVersionData] = useState({...emptyVersionData});
    const [originalData, setOriginaldata] = useState()
    const [isFormDirty, setIsFormDirty] = useState(false);

    const toggleCountriesModal = () => setCountriesModalOpen(!countriesModal);

    const toggleOrganizationsModal = () =>  setOrganizationsModalOpen(!organizationsModal)

    const selectVersion = (id) => {
        const urlId = id === 'new-version' ? 'new' : id;
        navigate(`/movies/${movie._id}/version/${urlId}`);
    };

    const closePanel = () => {
        navigate(`/movies/${movie._id}`);
        setNewVersionData({...emptyVersionData});
    };

    // Initialize form data from URL on mount or when URL changes
    useEffect(() => {
        if (versionId === 'new') {
            setNewVersionData({...emptyVersionData});
            setOriginaldata({...emptyVersionData});
        } else if (versionId && versions?.length) {
            const version = versions.find(v => v._id === versionId);
            if (version) {
                setNewVersionData({...version});
                setOriginaldata({...version});
            }
        }
    }, [versionId, versions]);

    const handleEdit = async (e) => {
        e.preventDefault();
        try {
            toast.loading('Updating version');
            await dispatch(updateVersion({
                movieId: movie._id,
                versionId: newVersionData._id,
                body: newVersionData,
            })).unwrap();
            toast.dismiss();
            toast.success('Version updated successfully');
            closePanel();
        } catch (error) {
            toast.dismiss();
            errorToast(error || 'Error updating version');
            console.error(error);
        }
    };

    const handleDelete = async (versionId) => {
        try {
            const promise = dispatch(deleteVersion({ movieId: movie._id, versionId })).unwrap();
            toast.promise(promise, {
                pending: 'Deleting version',
                success: 'Version deleted successfully',
                error: 'Error deleting version',
            });
            await promise;
            setDeleteModal({ isOpen: false, version: null });
            if (selectedVersionId === versionId) {
                closePanel();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const showDeleteModal = (e, version) => {
        e.stopPropagation();
        setDeleteModal({ isOpen: true, version });
    };

    const handleModalClose = () => {
        setDeleteModal({ isOpen: false, version: null });
    };

    const handleChange = (e) => {
        if (e.length){
            let bulkData = {}
            e.forEach(element => {
                const { name, value } = element.target;
                bulkData = {...bulkData,[name]: value  }
            });
            setNewVersionData({...newVersionData,...bulkData});
        }else {
            const { name, value } = e.target;
            setNewVersionData({ ...newVersionData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            toast.loading('Creating version');
            await dispatch(postNewVersion({ movieId: movie._id, body: newVersionData })).unwrap();
            toast.dismiss();
            toast.success('Version created successfully');
            closePanel();
        } catch (error) {
            toast.dismiss();
            errorToast(error || 'Error creating version');
            console.error(error);
        }
    };

    useEffect(()=>{
        dispatch(fetchOrganizations(token));
    },[dispatch, token])

    return (
        <Fragment>
            <ListGroup className="version-list-container mb-5">
                {versions?.map(version => (
                    <ListGroupItem
                        key={version._id}
                        className={`version-card bg-broken rounded text-white my-2${selectedVersionId === version._id ? ' selected' : ''}`}
                        onClick={() => selectVersion(version._id)}
                    >
                        <div className="d-flex gap-3">
                            <Avatar
                                imageUrl={version.distributor?.logo?.imageUrl}
                                name={version.distributor?.name || 'Distributor'}
                                icon={<span className="fas fa-warehouse" />}
                                size={48}
                            />

                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <strong className="fs-6">{version.name}</strong>
                                        {version.tier.map(t => <TierBadge key={t} tier={t} small />)}
                                    </div>
                                    <Button size="sm" color="danger" outline onClick={(e) => showDeleteModal(e, version)}>
                                        <span className="fas fa-trash" />
                                    </Button>
                                </div>

                                <div className="version-meta d-flex flex-wrap gap-2 align-items-center text-white-50 small">
                                    {version.releaseDate && (
                                        <span><span className="fas fa-calendar me-1" />{formatDate(version.releaseDate)}</span>
                                    )}
                                    {version.contentRestrictions && (
                                        <span className="badge bg-warning text-dark">{version.contentRestrictions}+</span>
                                    )}
                                    <span><span className="fas fa-globe me-1" />{formatRegions(version.countries)}</span>
                                    {version.isMovieOnRequest && (
                                        <span className="badge bg-info">MoR</span>
                                    )}
                                    {version.isAurora && (
                                        <span className="badge bg-purple">Aurora</span>
                                    )}
                                </div>

                                {version.availableForOrganizations?.length > 0 && (
                                    <div className="version-orgs d-flex gap-1 mt-2 flex-wrap">
                                        {version.availableForOrganizations.slice(0, 3).map(org => (
                                            <span key={org._id} className="org-mini-badge">
                                                {org.logo && <img src={imgUrl(org.logo.imageUrl)} alt="" />}
                                                {org.name}
                                            </span>
                                        ))}
                                        {version.availableForOrganizations.length > 3 && (
                                            <span className="text-white-50 small">+{version.availableForOrganizations.length - 3} more</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ListGroupItem>
                ))}
            </ListGroup>

            {/* Fixed Right Panel */}
            {selectedVersionId && (
                <div className="version-panel bg-broken shadow-lg">
                    <div className="version-panel-header">
                        <h5 className="mb-0">
                            {selectedVersionId === 'new-version' ? 'Create New Version' : 'Edit Version'}
                        </h5>
                        <button className="btn btn-link text-white" onClick={closePanel}>
                            <span className="fas fa-times fa-lg" />
                        </button>
                    </div>
                    <div className="version-panel-body">
                        <VersionForm
                            versionData={newVersionData}
                            originalData={originalData}
                            handleSubmit={selectedVersionId === 'new-version' ? handleSubmit : handleEdit}
                            handleChange={handleChange}
                            handleCancel={closePanel}
                            toggleCountriesModal={toggleCountriesModal}
                            countriesModal={countriesModal}
                            toggleOrganizationsModal={toggleOrganizationsModal}
                            organizationsModal={organizationsModal}
                            organizations={organizations}
                            distributor={distributor}
                            hideButtons
                            onDirtyChange={setIsFormDirty}
                        />
                    </div>
                    <div className="version-panel-footer">
                        <ButtonGroup className="w-100">
                            <Button color="primary" outline onClick={closePanel}>Cancel</Button>
                            <Button color="primary" type="submit" form="version-form">
                                {isFormDirty && <span className="unsaved-dot" />}
                                <span className="fas fa-save me-2"/>
                                {selectedVersionId === 'new-version' ? 'Create Version' : 'Save Changes'}
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            )}

            {deleteModal.version && (
                <Modal isOpen={deleteModal.isOpen} toggle={handleModalClose}>
                    <ModalHeader toggle={handleModalClose}>Confirm Deletion</ModalHeader>
                    <ModalBody>
                        Are you sure you want to delete this version?
                        <h3>{deleteModal.version.name}</h3>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" outline onClick={handleModalClose}>Cancel</Button>
                        <Button color="danger" onClick={() => handleDelete(deleteModal.version._id)}>Delete</Button>{' '}
                    </ModalFooter>
                </Modal>
            )}
        </Fragment>
    );
};

export default VersionsList;
